/**
 * HelpCenterPanel Component (AC-655)
 *
 * A sliding panel container for the in-app documentation help system.
 * Integrates SearchBar, DocNavigation, and DocContent components with
 * the helpStore for state management.
 *
 * Technical Design Specification:
 * - Sliding panel from right side (using transform translate-x)
 * - Fixed position, full height, w-96 width (384px)
 * - Header with "Help" title and close button (XMarkIcon)
 * - Child components: SearchBar (top), DocNavigation (left), DocContent (main)
 * - Integrates with helpStore for state management
 * - Backdrop overlay (semi-transparent) - click closes panel
 * - Escape key closes panel
 * - Focus trap - focus stays within panel when open
 * - Accessible: role="dialog", aria-modal="true", aria-labelledby
 *
 * @see AC-655 - Create HelpCenterPanel container component
 * @see AC-650 - Epic: In-App Documentation System
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';
import { useHelpStore } from '../../store/helpStore';
import { useTutorialStore } from '../../store/tutorialStore';
import docsService from '../../services/docsService';
import docsSearchService from '../../services/docsSearchService';
import SearchBar from './SearchBar';
import DocNavigation from './DocNavigation';
import DocContent from './DocContent';

/**
 * Props for the HelpCenterPanel component.
 */
export interface HelpCenterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when the panel should close */
  onClose: () => void;
}

/**
 * HelpCenterPanel - Sliding panel container for in-app documentation.
 */
const HelpCenterPanel: React.FC<HelpCenterPanelProps> = ({ isOpen, onClose }) => {
  // Get all store state and actions
  const store = useHelpStore();

  // Tutorial store for restart functionality - AC-675
  const { restartTutorial } = useTutorialStore();

  // Destructure state
  const {
    currentDocPath,
    searchQuery,
    searchResults,
    isSearching,
    docContent,
    isLoadingContent,
    docNavigation,
    // Actions
    setDocNavigation,
    setDocContent,
    setIsLoadingContent,
    setSearchQuery,
    setSearchResults,
    setIsSearching,
    openHelpToTopic,
    clearSearch,
  } = store;

  // Mobile navigation toggle state
  const [mobileNavVisible, setMobileNavVisible] = useState(false);

  // Refs
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  /**
   * Load navigation and initialize search on panel open.
   * Auto-loads the first topic if no topic is currently selected.
   */
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const nav = await docsService.loadNavigation();
        setDocNavigation(nav);

        // Auto-load first topic if no topic is currently selected (UX improvement)
        // This eliminates the empty state for first-time users
        if (!currentDocPath && nav.length > 0 && nav[0].items.length > 0) {
          openHelpToTopic(nav[0].items[0].path);
        }
      } catch (error) {
        // Handle error gracefully - panel remains functional
        console.error('Failed to load navigation:', error);
      }

      try {
        await docsSearchService.initialize();
      } catch (error) {
        // Handle error gracefully - search may be disabled
        console.error('Failed to initialize search:', error);
      }
    };

    loadData();
  }, [isOpen, setDocNavigation, currentDocPath, openHelpToTopic]);

  /**
   * Load document content when currentDocPath changes.
   */
  useEffect(() => {
    if (!isOpen || !currentDocPath) return;

    const loadContent = async () => {
      setIsLoadingContent(true);
      try {
        const content = await docsService.loadDocument(currentDocPath);
        setDocContent(content);
      } catch (error) {
        console.error('Failed to load document:', error);
        setDocContent(null);
      } finally {
        setIsLoadingContent(false);
      }
    };

    loadContent();
  }, [isOpen, currentDocPath, setDocContent, setIsLoadingContent]);

  /**
   * Handle search query changes.
   */
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = docsSearchService.search(query);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [setSearchQuery, setSearchResults, setIsSearching]
  );

  /**
   * Handle search result selection.
   */
  const handleResultSelect = useCallback(
    (path: string) => {
      openHelpToTopic(path);
      clearSearch();
    },
    [openHelpToTopic, clearSearch]
  );

  /**
   * Handle navigation item selection.
   */
  const handleNavSelect = useCallback(
    (path: string) => {
      openHelpToTopic(path);
    },
    [openHelpToTopic]
  );

  /**
   * Handle safe close - checks if onClose is defined.
   */
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  /**
   * Handle restart tutorial - closes help panel and starts tutorial.
   * AC-675
   */
  const handleRestartTutorial = useCallback(() => {
    handleClose();
    restartTutorial();
  }, [handleClose, restartTutorial]);

  /**
   * Handle Escape key to close panel.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // If there's a search query and we're in the search input, clear first
        // Otherwise close the panel
        const activeElement = document.activeElement;
        const isSearchInput = activeElement?.getAttribute('data-testid') === 'search-input';

        if (isSearchInput && searchQuery) {
          // Let the SearchBar handle this - it will clear the search
          return;
        }

        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, searchQuery, handleClose]);

  /**
   * Focus management - trap focus within panel and restore on close.
   */
  useEffect(() => {
    if (isOpen) {
      // Store current active element for focus restoration
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Move focus to close button on open
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  /**
   * Focus trap - keep focus within panel.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (event: FocusEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Focus escaped the panel - bring it back
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusTrap);
    return () => {
      document.removeEventListener('focusin', handleFocusTrap);
    };
  }, [isOpen]);

  // Don't render anything if not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div
        data-testid="help-panel-backdrop"
        className="fixed inset-0 bg-black/20 z-40"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-panel-title"
        className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white dark:bg-cap-dark shadow-xl border-l z-50 flex flex-col transform transition-transform duration-300 ease-in-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 id="help-panel-title" className="text-lg font-semibold text-gray-900 dark:text-warm-white">
            Help
          </h2>
          <div className="flex items-center space-x-2">
            {/* Restart Tutorial Button - AC-675 */}
            <Tooltip content="Restart Tutorial">
            <button
              data-testid="restart-tutorial-button"
              type="button"
              onClick={handleRestartTutorial}
              aria-label="Restart tutorial"
              className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              <span>Tutorial</span>
            </button>
            </Tooltip>
            {/* Close Button */}
            <button
              ref={closeButtonRef}
              data-testid="help-panel-close-button"
              type="button"
              onClick={handleClose}
              aria-label="Close help panel"
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md min-w-[48px] min-h-[48px] flex items-center justify-center"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b">
          <SearchBar
            onSearch={handleSearch}
            onResultSelect={handleResultSelect}
            results={searchResults}
            isSearching={isSearching}
            placeholder="Search documentation..."
          />
        </div>

        {/* Content Area - Navigation and DocContent */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Mobile: collapsible navigation toggle */}
          <div className="md:hidden border-b">
            <button
              onClick={() => setMobileNavVisible(!mobileNavVisible)}
              className="w-full px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 min-h-[48px] flex items-center justify-between"
              aria-expanded={mobileNavVisible}
              aria-controls="mobile-doc-nav"
            >
              <span>Sections</span>
              <span className="text-gray-400">{mobileNavVisible ? '\u25B2' : '\u25BC'}</span>
            </button>
            {mobileNavVisible && (
              <div id="mobile-doc-nav" className="border-t overflow-y-auto max-h-60">
                <DocNavigation
                  categories={docNavigation}
                  currentPath={currentDocPath}
                  onSelect={(path: string) => {
                    handleNavSelect(path);
                    setMobileNavVisible(false);
                  }}
                  isLoading={false}
                />
              </div>
            )}
          </div>

          {/* Desktop: fixed sidebar navigation */}
          <div className="hidden md:flex w-60 border-r overflow-y-auto flex-shrink-0">
            <DocNavigation
              categories={docNavigation}
              currentPath={currentDocPath}
              onSelect={handleNavSelect}
              isLoading={false}
            />
          </div>

          {/* Main Content Area */}
          <div className="overflow-y-auto p-4 flex-1">
            <DocContent
              content={docContent}
              isLoading={isLoadingContent}
              onLinkClick={handleNavSelect}
              className="flex-1 flex-grow"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpCenterPanel;
